import { useEffect, useRef, useState } from "react";
import { agents, productSteps } from "../content";
import { ArrowIcon, CheckIcon, RefreshIcon, ShieldIcon } from "../icons";
import type { DemoStep, Locale } from "../types";

type SimulatorProps = {
  locale: Locale;
};

const stepIds: DemoStep[] = ["intent", "strategy", "preview", "confirm", "execute"];

function initialIntent(locale: Locale) {
  return locale === "zh"
    ? "把5,000 USDC自动找最高收益，最大回撤不超过3%"
    : "Automatically find the highest yield for 5,000 USDC, with a maximum drawdown of no more than 3%.";
}

export default function Simulator({ locale }: SimulatorProps) {
  const [step, setStep] = useState<DemoStep>("intent");
  const [intent, setIntent] = useState(initialIntent(locale));
  const [running, setRunning] = useState(false);
  const [complete, setComplete] = useState(false);
  const timer = useRef<number | null>(null);
  const activeIndex = stepIds.indexOf(step);

  useEffect(() => {
    if (timer.current !== null) {
      window.clearTimeout(timer.current);
      timer.current = null;
    }
    setIntent(initialIntent(locale));
    setStep("intent");
    setRunning(false);
    setComplete(false);
  }, [locale]);

  useEffect(() => {
    return () => {
      if (timer.current !== null) {
        window.clearTimeout(timer.current);
      }
    };
  }, []);

  const goTo = (nextStep: DemoStep) => {
    setStep(nextStep);
    setComplete(false);
  };

  const confirmAndExecute = () => {
    setStep("execute");
    setRunning(true);
    setComplete(false);
    timer.current = window.setTimeout(() => {
      setRunning(false);
      setComplete(true);
      timer.current = null;
    }, 1500);
  };

  const reset = () => {
    setStep("intent");
    setIntent(initialIntent(locale));
    setRunning(false);
    setComplete(false);
  };

  return (
    <div className="demo-shell">
      <div className="demo-topbar">
        <span>AgentNexus</span>
        <strong>{locale === "zh" ? "产品演示" : "Product Demo"}</strong>
        <span>{String(activeIndex + 1).padStart(2, "0")} / 05</span>
      </div>

      <ol className="demo-steps" aria-label={locale === "zh" ? "产品演示步骤" : "Product demo steps"}>
        {productSteps.map((item, index) => (
          <li key={item.en} className={index === activeIndex ? "is-active" : index < activeIndex ? "is-complete" : ""}>
            <span>{index < activeIndex ? <CheckIcon /> : index + 1}</span>
            <small>{item[locale]}</small>
          </li>
        ))}
      </ol>

      <div className="demo-stage" aria-live="polite">
        {step === "intent" ? (
          <div className="demo-panel intent-panel">
            <label htmlFor="agent-intent">{locale === "zh" ? "输入你的目标" : "Enter your goal"}</label>
            <div className="chat-input">
              <textarea
                id="agent-intent"
                value={intent}
                onChange={(event) => setIntent(event.target.value)}
                rows={4}
              />
              <span>{intent.length} / 180</span>
            </div>
            <div className="demo-actions is-end">
              <button type="button" className="primary-button" disabled={!intent.trim()} onClick={() => goTo("strategy")}>
                {locale === "zh" ? "生成策略" : "Generate strategy"}
                <ArrowIcon />
              </button>
            </div>
          </div>
        ) : null}

        {step === "strategy" ? (
          <div className="demo-panel">
            <div className="agent-orchestration">
              <div className="orchestration-core">
                <span>AI</span>
                <small>{locale === "zh" ? "AI生成策略" : "AI generates the strategy"}</small>
              </div>
              <div className="agent-nodes">
                {agents.map((agent, index) => (
                  <div key={agent.en}>
                    <span>0{index + 1}</span>
                    <strong>{agent[locale]}</strong>
                  </div>
                ))}
              </div>
            </div>
            <dl className="strategy-summary">
              <div><dt>{locale === "zh" ? "目标" : "Objective"}</dt><dd>{locale === "zh" ? "自动寻找最高收益" : "Automatically find the highest yield"}</dd></div>
              <div><dt>{locale === "zh" ? "金额" : "Amount"}</dt><dd>5,000 USDC</dd></div>
              <div><dt>{locale === "zh" ? "风险边界" : "Risk boundary"}</dt><dd>{locale === "zh" ? "最大回撤不超过3%" : "Maximum drawdown no more than 3%"}</dd></div>
            </dl>
            <DemoActions
              locale={locale}
              onBack={() => goTo("intent")}
              onNext={() => goTo("preview")}
              nextLabel={locale === "zh" ? "查看收益预估与风险控制" : "Review projected returns and risk controls"}
            />
          </div>
        ) : null}

        {step === "preview" ? (
          <div className="demo-panel">
            <div className="preview-metrics">
              <div>
                <small>{locale === "zh" ? "收益预估" : "Projected return"}</small>
                <strong>{locale === "zh" ? "执行前清楚显示" : "Clearly displayed before execution"}</strong>
              </div>
              <div className="risk-metric">
                <small>{locale === "zh" ? "最大回撤" : "Maximum drawdown"}</small>
                <strong>≤ 3%</strong>
                <span>{locale === "zh" ? "风险控制条件" : "Risk-control condition"}</span>
              </div>
            </div>
            <div className="risk-control-line">
              <ShieldIcon />
              <span>{locale === "zh" ? "清楚显示收益预估与风险控制" : "Projected returns and risk controls are clearly displayed"}</span>
            </div>
            <DemoActions
              locale={locale}
              onBack={() => goTo("strategy")}
              onNext={() => goTo("confirm")}
              nextLabel={locale === "zh" ? "下一步" : "Next"}
            />
          </div>
        ) : null}

        {step === "confirm" ? (
          <div className="demo-panel confirmation-panel">
            <div className="confirm-message">
              <small>{locale === "zh" ? "请确认交易目标" : "Confirm the trading goal"}</small>
              <blockquote>“{intent}”</blockquote>
            </div>
            <div className="confirmation-flow">
              {agents.map((agent, index) => (
                <span key={agent.en}>{index + 1}. {agent[locale]}</span>
              ))}
            </div>
            <div className="demo-actions">
              <button type="button" className="quiet-button" onClick={() => goTo("preview")}>
                {locale === "zh" ? "返回" : "Back"}
              </button>
              <button type="button" className="primary-button" onClick={confirmAndExecute}>
                {locale === "zh" ? "一键确认" : "Confirm with one click"}
                <ArrowIcon />
              </button>
            </div>
          </div>
        ) : null}

        {step === "execute" ? (
          <div className="demo-panel execution-panel">
            <div className={`execution-orb ${complete ? "is-complete" : ""}`}>
              {complete ? <CheckIcon /> : <span />}
            </div>
            <h3>
              {running
                ? locale === "zh" ? "Agent自动执行" : "Agents execute automatically"
                : locale === "zh" ? "执行完成，即时回报结果" : "Execution complete. Results reported immediately."}
            </h3>
            <div className="execution-status">
              {agents.map((agent, index) => (
                <span key={agent.en} className={complete || index <= 2 ? "is-done" : ""}>
                  <CheckIcon /> {agent[locale]}
                </span>
              ))}
            </div>
            {complete ? (
              <button type="button" className="secondary-button" onClick={reset}>
                <RefreshIcon />
                {locale === "zh" ? "重新演示" : "Run the demo again"}
              </button>
            ) : null}
          </div>
        ) : null}
      </div>
    </div>
  );
}

function DemoActions({
  locale,
  onBack,
  onNext,
  nextLabel,
}: {
  locale: Locale;
  onBack: () => void;
  onNext: () => void;
  nextLabel: string;
}) {
  return (
    <div className="demo-actions">
      <button type="button" className="quiet-button" onClick={onBack}>
        {locale === "zh" ? "返回" : "Back"}
      </button>
      <button type="button" className="primary-button" onClick={onNext}>
        {nextLabel}
        <ArrowIcon />
      </button>
    </div>
  );
}
